/**
 * @file Sidebar.jsx
 * Barra di navigazione laterale dell'applicazione.
 *
 * - NAV_GROUPS: struttura dichiarativa dei gruppi e voci di menu con visibilità per ruolo.
 * - NavItem: singolo link con stile attivo basato su pathname corrente.
 * - NavGroup: gruppo collassabile di voci di menu.
 * - Sidebar: componente principale; filtra gruppi e voci visibili in base al ruolo utente.
 *   Si nasconde completamente (w-0) quando isSidebarCollapsed è true.
 */

import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useApp } from '../../hooks/useApp';
import { usePermissions } from '../../hooks/usePermissions';
import { cn } from '../../lib/utils/cn';
import { ROLES } from '../../lib/constants/roles';

/**
 * Struttura di navigazione con visibilità per ruolo.
 * roles: array di ROLES che possono vedere il gruppo / la voce.
 */
const ALL = [ROLES.ADMIN, ROLES.PLANNER, ROLES.PM_RICHIEDENTE, ROLES.RICHIEDENTE, ROLES.HR, ROLES.USER];
const MGMT = [ROLES.ADMIN, ROLES.PLANNER];
const EDITORS = [ROLES.ADMIN, ROLES.PM_RICHIEDENTE, ROLES.RICHIEDENTE, ROLES.HR];
const SETUP = [ROLES.ADMIN, ROLES.HR];
const SETUP_WITH_PLANNER = [...SETUP, ROLES.PLANNER];
const SETUP_WITH_PM = [...SETUP_WITH_PLANNER, ROLES.PM_RICHIEDENTE];

const NAV_GROUPS = [
  {
    group: 'Pianificazione',
    roles: ALL,
    items: [
      { to: '/planning',        icon: '📅', label: 'Richieste Risorse',            roles: EDITORS },
      //TODO: Necessarie ulteriori verifiche { to: '/excel',           icon: '🧾', label: 'Ultima Richiesta Risorse',      roles: EDITORS },
      { to: '/merge',           icon: '🧬', label: 'Lista Merge',               permission: 'canMerge', notActiveWhen: (pathname, state) => pathname.startsWith('/merge/') && state?.fromMergeGrid === true },
      { to: '/merge-grid',      icon: '📋', label: 'Ultimo Merge',              permission: 'canMerge', alsoActiveOn: (pathname, state) => pathname.startsWith('/merge/') && state?.fromMergeGrid === true },
      { to: '/pianificazione-definitiva', icon: '🌟', label: 'Pianificazione Definitiva', roles: ALL },
    ],
  },
  {
    group: 'Report & KPI',
    roles: ALL,
    items: [
      { to: '/dashboard', icon: '📊', label: 'Dashboard Sintesi', permission: 'canSeeDashboard' },
      { to: '/dashboard-bu', icon: '🏢', label: 'Dashboard BU',   permission: 'canSeeDashboardBu' },
      { to: '/critical',  icon: '⚠️', label: 'Criticità',         permission: 'canSeeCritical' },
    ],
  },
  {
    group: 'Team & Attività',
    roles: EDITORS,
    items: [
      { to: '/workbench',           icon: '🌳', label: 'Workbench',                   roles: EDITORS, permission: 'canSeeTeam' },
      { to: '/setup/projects',      icon: '🗂️', label: 'Team',                        roles: EDITORS, permission: 'canSeeTeam' },
      { to: '/setup/tasks',         icon: '🧱', label: 'Task',                        roles: EDITORS, permission: 'canSeeTeam' },
      { to: '/setup/task-links',    icon: '🔗', label: 'Assegnazioni Task → Risorse', roles: EDITORS, permission: 'canSeeTeam' },
      { to: '/setup/task-patterns', icon: '🧩', label: 'Pattern Impiego Risorse',     roles: MGMT,    permission: 'canSeeTeam' },
    ],
  },
  {
    group: 'Setup',
    roles: [...SETUP_WITH_PM, ROLES.RICHIEDENTE, ROLES.USER, ROLES.SALES_BACKOFFICE],
    items: [
      { to: '/setup/risorse',          icon: '👥', label: 'Risorse',                     roles: SETUP },
      { to: '/setup/aree',             icon: '🧩', label: 'Aree',                        roles: SETUP },
      { to: '/setup/business-units',   icon: '🏢', label: 'Business Units',              roles: SETUP },
      { to: '/setup/sedi',             icon: '📍', label: 'Sedi',                        roles: SETUP },
      { to: '/setup/project-managers', icon: '🧑‍💼', label: 'Project Managers',            roles: SETUP },
      { to: '/setup/senior-management',icon: '🧑‍💼',label: 'Senior Management',          roles: SETUP_WITH_PLANNER },
      { to: '/setup/clienti',          icon: '🏷️', label: 'Clienti',                     roles: [...ALL, ROLES.SALES_BACKOFFICE] },
      { to: '/setup/non-working-days', icon: '⛔', label: 'Giorni non lavorativi',       roles: SETUP },
      { to: '/setup/deadlines',        icon: '⏳', label: 'Date limite pianificazione',  roles: [ROLES.ADMIN, ROLES.PLANNER, ROLES.PM_RICHIEDENTE, ROLES.RICHIEDENTE, ROLES.HR] },
    ],
  },
];

function NavItem({ to, icon, label, alsoActiveOn, notActiveWhen }) {
  const location = useLocation();
  const suppressed = notActiveWhen ? notActiveWhen(location.pathname, location.state) : false;
  const isActive = !suppressed && (
    location.pathname === to ||
    (to !== '/' && location.pathname.startsWith(to + '/')) ||
    (alsoActiveOn ? alsoActiveOn(location.pathname, location.state) : false)
  );

  return (
    <Link
      to={to}
      style={isActive
        ? { backgroundColor: 'var(--nav-active)', borderColor: 'var(--line)', color: 'var(--ink)' }
        : { color: 'var(--ink)' }
      }
      className={cn(
        'flex items-center gap-3 px-3 py-2 rounded-xl text-sm transition-colors border',
        isActive ? 'border font-medium' : 'border-transparent hover:bg-card-inner'
      )}
    >
      <span className="w-5 text-center text-[1.1rem] opacity-90">{icon}</span>
      <span className="whitespace-nowrap">{label}</span>
    </Link>
  );
}

function NavGroup({ title, defaultOpen = true, children }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="mb-2">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 py-2.5 border border-line bg-card-inner rounded-xl text-muted hover:bg-card transition-colors"
      >
        <span className="text-[11px] font-bold uppercase tracking-wider whitespace-nowrap">{title}</span>
        <span className={cn('text-[10px] transition-transform duration-200', !isOpen && '-rotate-90')}>▼</span>
      </button>
      {isOpen && (
        <div className="mt-1 space-y-0.5 px-1 pb-2">
          {children}
        </div>
      )}
    </div>
  );
}

export default function Sidebar() {
  const { userRole, userName, isSidebarCollapsed } = useApp();
  const { can } = usePermissions();
  const role = userRole ?? ROLES.PREDEFINITO;
  
  return (
    <aside
      className={cn(
        'h-screen bg-sidebar flex flex-col sticky top-0 shrink-0 overflow-hidden z-20 transition-all duration-300 ease-in-out border-r border-line',
        isSidebarCollapsed ? 'w-0 border-r-0 opacity-0' : 'w-72 opacity-100'
      )}
    >
      <div className="w-72 h-full flex flex-col">

        {/* Brand */}
        <div className="p-4 border-b border-line shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-3.5 h-3.5 rounded-full bg-gradient-to-tr from-orange-500 to-slate-900 shadow-sm ring-4 ring-orange-50 dark:ring-orange-950 shrink-0" />
            <div>
              <div className="font-semibold text-ink leading-tight whitespace-nowrap">Alfa Planning <span className="text-xs text-muted font-normal">v{__APP_VERSION__}</span></div>
              <div className="flex items-left gap-3">
                <div className="text-xs text-muted whitespace-nowrap">{userName}</div>
                <div className="text-xs text-muted whitespace-nowrap">Ruolo: {role}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Nav — filtrata per ruolo */}
        <nav className="flex-1 overflow-y-auto p-3 scrollbar-thin">
          {NAV_GROUPS.map(({ group, roles: groupRoles, items }) => {
            if (!groupRoles.includes(role)) return null;
            const visibleItems = items.filter(item =>
              item.permission ? can(item.permission) : item.roles?.includes(role)
            );
            if (visibleItems.length === 0) return null;

            return (
              <NavGroup key={group} title={group} defaultOpen>
                {visibleItems.map(item => (
                  <NavItem key={item.to} to={item.to} icon={item.icon} label={item.label} alsoActiveOn={item.alsoActiveOn} notActiveWhen={item.notActiveWhen} />
                ))}
              </NavGroup>
            );
          })}
        </nav>

      </div>
    </aside>
  );
}
